export const GROUPING_POLLING_STATES = {
  IDLE: 'idle',
  POLLING: 'polling',
  ERROR: 'error',
  FINISHED: 'finished'
};

export const GROUPING_POLL_INTERVAL_MS = 5000;
export const GROUPING_LAST_COMMAND_TIMEOUT_MS = 60000;
export const GROUPING_COMMAND_STATUS_TIMEOUT_MS = 60000;

export const GROUPING_SUPERSEDED_MESSAGE = 'Cancelled by a newer grouping request.';
export const GROUPING_TIMEOUT_MESSAGE = 'Grouping command was not created within 1 minute.';
export const GROUPING_STATUS_TIMEOUT_MESSAGE = 'Grouping command did not finish within 1 minute.';
export const GROUPING_NOT_TRIGGERED_MESSAGE = 'No grouping command appeared; the import likely had nothing to group.';

export const normalizeCommandIdentifier = (value) => {
  if (value === null || typeof value === 'undefined') return '';
  return String(value).trim();
};

export const toApiUtcDate = (date) => {
  if (!(date instanceof Date)) return '';
  return date.toISOString().split('.')[0];
};

export const extractCommandIdentifier = (payload = {}) => {
  return (
    payload?.command_id ??
    payload?.commandId ??
    payload?._id ??
    payload?.id ??
    payload?.metadata?.command_id ??
    payload?.metadata?._id ??
    payload?.data?.command_id ??
    payload?.data?._id ??
    null
  );
};

export const extractCommandCreatedAt = (payload = {}) => {
  return (
    payload?.metadata?.create_time ??
    payload?.create_time ??
    payload?.created_at ??
    payload?.createdAt ??
    payload?.data?.metadata?.create_time ??
    payload?.data?.create_time ??
    payload?.data?.created_at ??
    payload?.data?.createdAt ??
    null
  );
};

export const parseCommandTimestamp = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const parsedValue = Date.parse(value);
  return Number.isNaN(parsedValue) ? null : parsedValue;
};

export const isCommandNewerThanSince = (payload = {}, sinceTimestamp) => {
  const createdAt = extractCommandCreatedAt(payload);
  const commandTimestamp = parseCommandTimestamp(createdAt);

  if (!commandTimestamp) return true;
  return commandTimestamp >= sinceTimestamp;
};

export const isCommandInProgress = (duration, status) => {
  const normalizedStatus = String(status ?? '').trim().toLowerCase();
  if (normalizedStatus) {
    return ['pending', 'queued', 'running', 'processing', 'in progress'].includes(normalizedStatus);
  }

  const normalizedDuration = String(duration ?? '').trim().toLowerCase();
  return normalizedDuration === '' || normalizedDuration === 'in progress';
};

export const isCommandError = (duration, status) => {
  const normalizedStatus = String(status ?? '').trim().toLowerCase();
  if (normalizedStatus) {
    return ['error', 'failed', 'cancelled', 'canceled'].includes(normalizedStatus);
  }

  const normalizedDuration = String(duration ?? '').trim().toLowerCase();
  return normalizedDuration === 'error' || normalizedDuration === 'failed';
};

export const isCommandFinished = (payload = {}) => {
  return !isCommandInProgress(payload?.duration, payload?.status);
};

export const getGroupingWorkspaceName = (command = {}) => (
  command.workspaceName ||
  command.workspace_name ||
  command.workspace ||
  command.name ||
  ''
);

export const isGroupingQueueCommand = (command = {}) => Boolean(command?.isGrouping);

export const getGroupingCommandTarget = (command = {}) => ({
  queuedAt: command?.queuedAt
});

export const normalizeGroupingStartTimestamp = (command = {}) => {
  const timestamp = Number(command?.groupingStartTimestamp ?? command?.queuedAt ?? Date.now());
  return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : Date.now();
};

export const createGroupingTimeoutError = (message = GROUPING_TIMEOUT_MESSAGE) => {
  const error = new Error(message);
  error.name = 'GroupingPollingTimeoutError';
  error.code = 'GROUPING_TIMEOUT';
  return error;
};

export const isGroupingTimeoutError = (error) => error?.code === 'GROUPING_TIMEOUT';

export const createGroupingNotTriggeredError = (message = GROUPING_NOT_TRIGGERED_MESSAGE) => {
  const error = new Error(message);
  error.name = 'GroupingNotTriggeredError';
  error.code = 'GROUPING_NOT_TRIGGERED';
  return error;
};

export const isGroupingNotTriggeredError = (error) => error?.code === 'GROUPING_NOT_TRIGGERED';
